import type { UseQueryResult } from '@tanstack/react-query';

import { TriasHelper } from '@/modules/TriasHelper';
import type { GeoPositionStructure, RouteStructure } from '@/modules/api-routing';
import { IndividualTripParamStructure } from '@/modules/api-routing';
import { RouteLineType } from '@/types/RouteLineType';
import type { RouteSegmentFeature, RouteSegmentsFeatureCollection } from '@/types/RouteSegmentFeature';
import type { Trias } from '@/types/Trias';
import { isNonNullable } from '@/utils/isNonNullable';

export class TriasRouteSegments {
  public static get(route: UseQueryResult<Trias>, structureIndex: number) {
    const routeStructure = TriasHelper.getIndividualRouteStructure(route.data, structureIndex);
    const algorithm = routeStructure?.extension?.any.value.modeMetaInfo[0].tripModeAlgorithm.individualAlgorithm[0];

    return TriasRouteSegments.getCar(routeStructure, algorithm);
  }

  private static getCar(route: RouteStructure | undefined, algorithm?: IndividualTripParamStructure.algorithmType) {
    const featureCollection: RouteSegmentsFeatureCollection = {
      type: 'FeatureCollection',
      features: [],
    };

    if (route === undefined) return featureCollection;

    let defaultRouteLineType: RouteLineType;

    if (algorithm === IndividualTripParamStructure.algorithmType.BEAUTIFUL) {
      defaultRouteLineType = RouteLineType.EMERGENCY_SERVICES;
    } else if (algorithm === IndividualTripParamStructure.algorithmType.OPTIMAL) {
      defaultRouteLineType = RouteLineType.STRATEGY_AVOID;
    } else {
      defaultRouteLineType = RouteLineType.STRATEGY_PREFERRED;
    }

    const segments = route?.routeLeg
      ?.map((leg) => leg.legTrack?.trackSection?.[0].projection?.position)
      .flat()
      .filter(isNonNullable);

    if (segments) {
      featureCollection.features.push(
        TriasRouteSegments.getSegmentSlice(segments, 0, segments.length, defaultRouteLineType),
      );
    }

    return featureCollection;
  }

  private static getSegmentSlice(
    segments: GeoPositionStructure[],
    start: number,
    end: number,
    routeLineType: RouteLineType,
  ): RouteSegmentFeature {
    return {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: segments
          .slice(start, end)
          .map(({ longitude, latitude }) => [longitude as number, latitude as number]),
      },
      properties: { routeLineType },
    };
  }
}
